import React from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import Button, { ButtonVariants } from '#web-components/components/Button';
import InlineButton from '#web-components/components/InlineButton';
import Typography from '#web-components/components/Typography';
import { EditIcon } from '#web-components/components/Icons';
import FlashMessage from '#web-components/components/FlashMessage';

import styles from './ChannelItem.styles';

const useStyles = makeStyles(styles, { name: 'ChannelItem' });

interface ChannelItemProps {
  label: string;
  icon: React.ElementType;
  isActive: boolean;
  channel?: string;
  placeholder?: React.ReactNode | string;
  onEdit?: () => void;
  onActivate?: () => void;
  onDeactivate?: () => void;
  text: {
    hint: React.ReactNode | string;
    hintActive: string;
  }
}

export default function ChannelItem({
  label,
  channel,
  placeholder,
  icon: Icon,
  text,
  isActive,
  onEdit,
  onActivate,
  onDeactivate,
}: ChannelItemProps) {
  const classes = useStyles();
  const { t } = useTranslation('pages', { keyPrefix: 'profile' });

  return (
    <Box className={classes.root}>
      <Typography variant="textSmall" className={classes.label}>{label}</Typography>
      <Box display="flex" className={classes.box}>

        {!channel && (
          <Box ml={8} mt={2}>
            { placeholder }
          </Box>
        )}

        {channel && (
          <>
            <Box className={clsx(classes.icon)}><Icon /></Box>
            <Box className={classes.boxChannel}>
              <Box className={classes.channel}>
                <Typography
                  variant="h4"
                  className={clsx(classes.channelName)}
                >
                  {channel}
                </Typography>
              </Box>

              {isActive && (
                <Box className={classes.hint}>
                  <FlashMessage
                    status="success"
                    message={text.hintActive}
                    hideIcon
                  />
                </Box>
              )}

              {!isActive && (
                <Box className={classes.hint}>
                  <FlashMessage
                    status="warning"
                    message={text.hint}
                    hideIcon
                  />
                </Box>
              )}

              <div className={classes.actionsBlock}>
                <Button
                  onClick={isActive ? onDeactivate : onActivate}
                  variant={isActive ? ButtonVariants.secondary : ButtonVariants.primary}
                  data-xpath={`${channel}.${isActive ? 'settings.turnOffMessage' : 'settings.allowMessages'}`}
                >
                  {isActive ? t('settings.turnOffMessage') : t('settings.allowMessages')}
                </Button>
                {
                  onEdit && (
                    <InlineButton
                      onLinkClick={onEdit}
                      classes={{ link: classes.editButton }}
                      leftIcon={<EditIcon />}
                    >
                      { t('settings.edit') }
                    </InlineButton>
                  )
                }
              </div>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
